import {useEffect, useMemo, useRef, useState} from 'react';

import {Spinner} from './ui';
import {C, WIZARD_FRAMINGS} from '../constants';
import {AI_DEFAULTS} from '../config';
import {requestJson} from '../services/ai/aiService';

// ── Generate smart row suggestions from product vars + brand ────────

const SKIP_VARS = new Set(['sku', 'handle', 'status', 'vendor', 'collection', 'description', 'product_name']);

const ANGLE_MAP = [
    {field: 'Material & build', match: ['material', 'finish', 'hardware_material'], kw: (vs) => vs.map((v) => `{{${v.name}}}`).join(', ') + ', durable construction'},
    {field: 'Design', match: ['style', 'design', 'color', 'color_family'], kw: (vs) => vs.map((v) => `{{${v.name}}}`).join(', ') + ', modern aesthetic'},
    {field: 'Specifications', match: ['weight', 'dimensions', 'size', 'pack_size', 'gauge'], kw: (vs) => vs.map((v) => `{{${v.name}}}`).join(', ')},
    {field: 'Installation', match: ['installation', 'installation_type', 'mount', 'door_intended_use', 'assembly_required'], kw: (vs) => vs.map((v) => `{{${v.name}}}`).join(', ') + ', easy setup'},
    {field: 'Value', match: ['price', 'compare_at_price'], kw: (vs) => vs.map((v) => `{{${v.name}}}`).join(', ') + ', premium quality'},
    {field: 'Warranty & trust', match: ['warranty'], kw: (vs) => vs.map((v) => `{{${v.name}}}`).join(', ') + ', guaranteed'},
    {field: 'Compatibility', match: ['compatible_door_thickness', 'bore_hole_diameter', 'backset', 'faucet_connection_size'], kw: (vs) => vs.map((v) => `{{${v.name}}}`).join(', ') + ', universal fit'},
    {field: 'Certifications', match: ['ada_compliant', 'cupc_certified', 'csa_certified', 'lead_free', 'epa_watersense_certified'], kw: (vs) => vs.map((v) => `${v.name.replace(/_/g, ' ')}: {{${v.name}}}`).join(', ')},
];

function buildSmartSuggestions(vars, brandProfile) {
    if (!vars?.length) return [];
    const varMap = new Map(vars.map((v) => [v.name, v]));
    const suggestions = [];

    for (const tpl of ANGLE_MAP) {
        const matched = tpl.match.map((name) => varMap.get(name)).filter(Boolean);
        if (matched.length) {
            suggestions.push({
                field: tpl.field,
                kw: tpl.kw(matched),
                preview: matched.map((v) => `${v.name.replace(/_/g, ' ')}: ${v.sample}`).join(' · '),
            });
        }
    }

    const typeVar = varMap.get('product_type') || varMap.get('category');
    if (typeVar) {
        suggestions.push({
            field: 'Ideal for',
            kw: `{{${typeVar.name}}}, ${brandProfile?.targetAudience || 'homeowners, professionals'}`,
            preview: typeVar.sample,
        });
    }

    suggestions.push({
        field: 'Key benefit',
        kw: brandProfile?.wordsLove?.slice(0, 3).join(', ') || 'premium performance, quality',
        preview: 'Brand-driven benefit angle',
    });

    const optionVars = vars.filter((v) => ['orientation', 'pack_size', 'variant_title'].includes(v.name) && v.sample);
    if (optionVars.length) {
        suggestions.push({
            field: 'Variant detail',
            kw: optionVars.map((v) => `{{${v.name}}}`).join(', '),
            preview: optionVars.map((v) => `${v.name.replace(/_/g, ' ')}: ${v.sample}`).join(' · '),
        });
    }

    return suggestions;
}

// ── AI suggestion generation (bullets or FAQs) ──────────────────────

export async function generateAiAngles({vars, brandProfile, product, count, wizardMode}) {
    const category = product?.category || 'General';

    // List available variable NAMES with their meaning (from name), not values
    const productVars = (vars || []).filter((v) => v.sample && !SKIP_VARS.has(v.name));
    const varList = productVars.map((v) => `- {{${v.name}}} (${v.name.replace(/_/g, ' ')})`).join('\n');

    const brandContext = brandProfile ? [
        brandProfile.companyName ? `Brand: ${brandProfile.companyName}` : '',
        brandProfile.targetAudience ? `Audience: ${brandProfile.targetAudience}` : '',
    ].filter(Boolean).join('\n') : '';

    const isFaqs = wizardMode === 'faqs';
    const unitLabel = isFaqs ? 'FAQ topic' : 'bullet point angle';
    const intent = isFaqs
        ? 'Each FAQ topic must answer a real buyer concern this product would face — sizing, fit, material, care, compatibility, use cases, durability, shipping, warranty, or differentiation. Avoid duplicating what the title, description, or bullets would already cover.'
        : 'Every angle must be relevant to the category specifically. Each bullet covers a different angle.';

    const prompt = `You are an e-commerce content strategist. Generate exactly ${count} ${unitLabel}s for a "${category}" product listing.

IMPORTANT: This product is a "${category}" — ONLY generate ${unitLabel}s relevant to ${category}. Do NOT include topics from other product categories.

${brandContext}

Available template variables for this product:
${varList || 'None'}

Return ONLY a JSON array, no markdown:
[{"field": "${isFaqs ? 'Topic Name' : 'Angle Name'}", "kw": "${isFaqs ? 'answer context, key buyer concerns' : '{{var}}, keyword'}"}, ...]

Rules:
- ${intent}
- Use {{variable_name}} syntax for product-specific data when relevant — never literal values
- These templates are reused across multiple variants, so keep them generic
- ${isFaqs ? 'Each topic should drive a distinct Q&A — no overlap.' : 'Add 1-2 category-relevant SEO keywords per bullet.'}
- Order from most important to least`;

    return requestJson({
        prompt,
        model: AI_DEFAULTS.model,
        maxTokens: 1500,
        temperature: 0.4,
    });
}

// ── Manual wizard angles ────────────────────────────────────────────

const BULLET_MANUAL_ANGLES = [
    {label: 'Key benefit', field: 'Key benefit', hint: "What's the #1 thing it does for the buyer?"},
    {label: 'Material', field: 'Material', hint: 'What is it made of?'},
    {label: 'Design', field: 'Design style', hint: 'How does it look and feel?'},
    {label: 'Ideal customer', field: 'Ideal for', hint: 'Who is this perfect for?'},
    {label: 'Value', field: 'Value', hint: 'Why is the price justified?'},
    {label: 'Unique edge', field: 'What sets it apart', hint: 'What can competitors not claim?'},
];

const FAQ_MANUAL_ANGLES = [
    {
        label: 'Sizing & fit',
        field: 'Sizing & fit',
        kw: 'true-to-size guidance, fit notes, when to size up or down',
        hint: 'Does it run true to size? Fit guidance.',
    },
    {
        label: 'Material & care',
        field: 'Material & care',
        kw: 'materials, finish, washing or maintenance instructions',
        hint: 'What is it made of? How do I care for it?',
    },
    {
        label: 'Compatibility',
        field: 'Compatibility',
        kw: 'works with which products, standards, or installations',
        hint: 'Does it fit / work with X?',
    },
    {
        label: 'Shipping & returns',
        field: 'Shipping & returns',
        kw: 'shipping windows, return policy, exchange rules',
        hint: 'How long does shipping take? Returns?',
    },
    {
        label: 'Performance & durability',
        field: 'Performance & durability',
        kw: 'expected lifespan, daily-use durability, real-world performance',
        hint: 'How long does it last under daily use?',
    },
    {
        label: 'Warranty',
        field: 'Warranty',
        kw: 'warranty term, what is covered, how to claim',
        hint: 'What warranty is included?',
    },
    {
        label: 'Use cases',
        field: 'Use cases',
        kw: 'best-suited environments and scenarios for this product',
        hint: 'When and where is this best used?',
    },
    {
        label: 'How it compares',
        field: 'How it compares',
        kw: 'key differentiators vs typical alternatives in the category',
        hint: 'How is this different from alternatives?',
    },
];

// ── Component ───────────────────────────────────────────────────────

export default function PromptWizard({
    onFill,
    onClose,
    brandProfile,
    vars,
    product,
    wizardMode = 'bullets',
}) {
    const isFaqs = wizardMode === 'faqs';
    const manualAngles = isFaqs ? FAQ_MANUAL_ANGLES : BULLET_MANUAL_ANGLES;

    const [mode, setMode] = useState('manual');
    const [manualStep, setManualStep] = useState(1);
    const [angle, setAngle] = useState(null);
    const [framing, setFraming] = useState(null);
    const [proof, setProof] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [aiResults, setAiResults] = useState(null);
    const [aiError, setAiError] = useState('');
    const aiTriggered = useRef(false);

    const smartSuggestions = useMemo(() => buildSmartSuggestions(vars, brandProfile), [vars, brandProfile]);

    const fillSmart = (suggestion) => {
        onFill({field: suggestion.field, kw: suggestion.kw});
        onClose();
    };

    // FAQ mode fills the row directly from the angle pick — the
    // bullet-style 3-step manual flow (angle → framing → detail) doesn't
    // map onto question/answer topics. Bullets mode keeps the full flow.
    const fillFaqAngle = (faqAngle) => {
        onFill({field: faqAngle.field, kw: faqAngle.kw || ''});
        onClose();
    };

    const fillManual = () => {
        onFill({
            field: angle?.field || '',
            kw: [framing?.prefix, proof].filter(Boolean).join(', '),
        });
        onClose();
    };

    const runAi = async () => {
        setAiLoading(true);
        setAiError('');
        setAiResults(null);
        try {
            const results = await generateAiAngles({
                vars,
                brandProfile,
                product,
                count: 8,
                wizardMode,
            });
            setAiResults(results);
        } catch (err) {
            setAiError(err.message || 'Failed to generate');
        } finally {
            setAiLoading(false);
        }
    };

    // Auto-trigger AI when switching to AI tab
    useEffect(() => {
        if (mode === 'ai' && !aiResults && !aiLoading && !aiTriggered.current) {
            aiTriggered.current = true;
            runAi();
        }
    }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={{
            margin: '2px 0 4px 0',
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            overflow: 'hidden',
            animation: 'popIn .15s ease both',
            boxShadow: `0 2px 12px ${C.shadow}`,
        }}>
            {/* Header with mode tabs */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '7px 10px',
                background: C.surfaceAlt,
                borderBottom: `1px solid ${C.border}`,
            }}>
                <div style={{display: 'flex', gap: 3}}>
                    {[
                        {key: 'manual', label: 'Manual'},
                        {key: 'ai', label: 'AI Suggest'},
                    ].map((m) => (
                        <button
                            key={m.key}
                            onClick={() => setMode(m.key)}
                            style={{
                                fontFamily: 'inherit',
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '3px 8px',
                                borderRadius: 5,
                                border: mode === m.key ? `1px solid ${C.teal}44` : '1px solid transparent',
                                background: mode === m.key ? `${C.teal}12` : 'transparent',
                                color: mode === m.key ? C.teal : C.textMuted,
                                cursor: 'pointer',
                            }}>
                            {m.label}
                        </button>
                    ))}
                </div>
                <button
                    onClick={onClose}
                    style={{border: 'none', background: 'transparent', color: C.textFaint, cursor: 'pointer', fontSize: 14, padding: '0 2px'}}>
                    ×
                </button>
            </div>

            <div style={{padding: '10px 12px'}}>
                {/* ── AI Suggest mode ── */}
                {mode === 'ai' && (
                    <div>
                        {aiLoading && (
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '20px 0', color: C.textMuted, fontSize: 12}}>
                                <Spinner /> Generating suggestions...
                            </div>
                        )}

                        {!aiResults && !aiLoading && aiError && (
                            <div>
                                <div style={{fontSize: 11, color: '#D85A30', marginBottom: 8}}>{aiError}</div>
                                <button
                                    onClick={() => { aiTriggered.current = false; runAi(); }}
                                    style={{
                                        fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
                                        padding: '7px 14px', borderRadius: 7, border: 'none',
                                        background: '#1D9E75', color: '#fff', cursor: 'pointer',
                                    }}>
                                    Retry
                                </button>
                            </div>
                        )}

                        {aiResults && (
                            <>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 6,
                                        maxHeight: 280,
                                        overflowY: 'auto',
                                        // Reserve space for the scrollbar so the
                                        // rightmost edge of each card doesn't
                                        // jump when content overflows.
                                        paddingRight: 2,
                                    }}>
                                    {aiResults.map((r, i) => (
                                        <button
                                            key={i}
                                            onClick={() => { onFill({field: r.field, kw: r.kw}); onClose(); }}
                                            style={{
                                                fontFamily: 'inherit',
                                                textAlign: 'left',
                                                padding: '8px 10px',
                                                borderRadius: 8,
                                                border: `1px solid ${C.border}`,
                                                background: 'transparent',
                                                cursor: 'pointer',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 4,
                                                alignItems: 'stretch',
                                            }}>
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: C.ink,
                                                    wordBreak: 'break-word',
                                                    lineHeight: 1.4,
                                                }}>
                                                {r.field}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: 10,
                                                    color: C.textFaint,
                                                    fontFamily: 'monospace',
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'normal',
                                                    lineHeight: 1.5,
                                                }}>
                                                {r.kw}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={() => { setAiResults(null); aiTriggered.current = false; runAi(); }}
                                    style={{
                                        fontFamily: 'inherit', fontSize: 10, fontWeight: 600, marginTop: 8,
                                        padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`,
                                        background: 'transparent', color: C.textMuted, cursor: 'pointer',
                                    }}>
                                    Regenerate
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* ── Manual mode ── */}
                {mode === 'manual' && (
                    <>
                        {/* FAQ manual mode: single-step picker. Each angle
                            already encodes both the topic and an answer
                            context hint — bullet-style framing/detail
                            steps don't apply to question/answer topics. */}
                        {isFaqs ? (
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5}}>
                                {manualAngles.map((a) => (
                                    <button
                                        key={a.label}
                                        onClick={() => fillFaqAngle(a)}
                                        style={{
                                            fontFamily: 'inherit',
                                            fontSize: 11,
                                            fontWeight: 500,
                                            padding: '7px 10px',
                                            borderRadius: 8,
                                            border: `1px solid ${C.border}`,
                                            background: 'transparent',
                                            color: C.ink,
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                        }}>
                                        {a.label}
                                        <span
                                            style={{
                                                display: 'block',
                                                fontSize: 9.5,
                                                color: C.textMuted,
                                                marginTop: 1,
                                            }}>
                                            {a.hint}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                        <>
                        <div style={{display: 'flex', gap: 6, marginBottom: 10}}>
                            {[{n: 1, label: 'Angle'}, {n: 2, label: 'Framing'}, {n: 3, label: 'Detail'}].map(({n, label}) => (
                                <button
                                    key={n}
                                    onClick={() => n <= manualStep && setManualStep(n)}
                                    style={{
                                        fontFamily: 'inherit',
                                        fontSize: 10,
                                        fontWeight: 600,
                                        padding: '3px 8px',
                                        borderRadius: 5,
                                        border: 'none',
                                        background: manualStep === n ? `${C.teal}14` : 'transparent',
                                        color: manualStep === n ? C.teal : n < manualStep ? C.textMuted : C.textFaint,
                                        cursor: n <= manualStep ? 'pointer' : 'default',
                                    }}>
                                    {n < manualStep ? '✓ ' : `${n}. `}{label}
                                </button>
                            ))}
                        </div>

                        {manualStep === 1 && (
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5}}>
                                {manualAngles.map((a) => (
                                    <button
                                        key={a.label}
                                        onClick={() => { setAngle(a); setManualStep(2); }}
                                        style={{
                                            fontFamily: 'inherit', fontSize: 11, fontWeight: 500, padding: '7px 10px', borderRadius: 8,
                                            border: `1px solid ${angle?.label === a.label ? `${C.purple}66` : C.border}`,
                                            background: angle?.label === a.label ? `${C.purple}10` : 'transparent',
                                            color: C.ink, cursor: 'pointer', textAlign: 'left',
                                        }}>
                                        {a.label}
                                        <span style={{display: 'block', fontSize: 9.5, color: C.textMuted, marginTop: 1}}>{a.hint}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {manualStep === 2 && (
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5}}>
                                {WIZARD_FRAMINGS.map((f) => (
                                    <button
                                        key={f.label}
                                        onClick={() => { setFraming(f); setManualStep(3); }}
                                        style={{
                                            fontFamily: 'inherit', fontSize: 11, fontWeight: 500, padding: '7px 10px', borderRadius: 8,
                                            border: `1px solid ${framing?.label === f.label ? `${C.blue}66` : C.border}`,
                                            background: framing?.label === f.label ? `${C.blue}10` : 'transparent',
                                            color: C.ink, cursor: 'pointer', textAlign: 'left',
                                        }}>
                                        {f.label}
                                        <span style={{display: 'block', fontSize: 9.5, color: C.textMuted, marginTop: 1}}>{f.eg}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {manualStep === 3 && (
                            <>
                                <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8}}>
                                    <span style={{fontSize: 12, color: C.textSoft, flexShrink: 0, fontStyle: 'italic'}}>"{framing?.prefix}</span>
                                    <input
                                        autoFocus
                                        value={proof}
                                        onChange={(e) => setProof(e.target.value.slice(0, 500))}
                                        onKeyDown={(e) => e.key === 'Enter' && proof.trim() && fillManual()}
                                        placeholder="type or pick below..."
                                        style={{
                                            flex: 1, fontFamily: 'inherit', fontSize: 12, padding: '7px 10px',
                                            border: `1px solid ${C.border}`, borderRadius: 7, color: C.ink, outline: 'none', background: C.surfaceAlt,
                                        }}
                                    />
                                    <span style={{fontSize: 12, color: C.textSoft, fontStyle: 'italic'}}>"</span>
                                </div>
                                {vars?.length > 0 && (
                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8}}>
                                        {vars.filter((v) => !SKIP_VARS.has(v.name) && v.sample && v.sample.length < 60).slice(0, 8).map((v) => (
                                            <button
                                                key={v.name}
                                                onClick={() => setProof((p) => p ? `${p}, {{${v.name}}}` : `{{${v.name}}}`)}
                                                style={{
                                                    fontFamily: 'inherit', fontSize: 10, padding: '3px 7px', borderRadius: 5,
                                                    border: `1px solid ${C.border}`, background: 'transparent', color: C.textSoft, cursor: 'pointer',
                                                }}>
                                                {`{{${v.name}}}`}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
                                    <button
                                        onClick={() => setManualStep(2)}
                                        style={{fontFamily: 'inherit', fontSize: 11, padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, cursor: 'pointer'}}>
                                        Back
                                    </button>
                                    <button
                                        disabled={!proof.trim()}
                                        onClick={fillManual}
                                        style={{fontFamily: 'inherit', fontSize: 11, fontWeight: 600, padding: '6px 12px', borderRadius: 7, border: 'none', background: proof.trim() ? '#1D9E75' : C.surfaceAlt, color: proof.trim() ? '#fff' : C.textMuted, cursor: proof.trim() ? 'pointer' : 'not-allowed'}}>
                                        Fill row
                                    </button>
                                </div>
                            </>
                        )}
                        </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
